'use client';

import {
	type Country,
	CountrySchema,
	type FileToUpload,
	type StepperRef,
	useCountries,
	useIntl
} from '@capsicum/common';
import { useMe, useUserAddressQuery } from '@capsicum/socios-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { PROOF_OF_ADDRESS_UPLOAD_PROPS } from '../constants';
import { useUpdateUserAddress } from '../data-access/queries/usePostalAddressQueries';
import { useCreateValidationRequest } from '../data-access/queries/useValidationRequestQueries';

export type PostalAddressVerificationFormType = z.infer<ReturnType<typeof usePostalAddressVerificationSchema>>;

const usePostalAddressVerificationSchema = () => {
	const { $t } = useIntl();

	return useMemo(
		() =>
			z.object({
				city: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.city.label'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						255,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.city.label'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					),
				country: CountrySchema,
				region: z.union([
					z.string().refine(val => val.length === 0 || (val.length >= 2 && val.length <= 255), {
						message: $t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.region.labelShort'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					}),
					z.null()
				]),
				street: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.street.label'),
							max: 512,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						512,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.street.label'),
							max: 512,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					),
				zipCode: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.postcode.label'),
							max: 50,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						50,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.postcode.label'),
							max: 50,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					)
			}),
		[$t]
	);
};

const usePostalAddressVerificationForm = () => {
	const { me } = useMe();
	const { countries } = useCountries();
	const userAddressCountry = useMemo(
		() => countries.find((country: Country) => country.iso2 === me.address?.countryCode),
		[countries, me.address?.countryCode]
	);

	const postalAddressVerificationForm = useForm<PostalAddressVerificationFormType>({
		defaultValues: useMemo(
			() => ({
				city: me.address?.city || '',
				country: userAddressCountry,
				region: me.address?.region || '',
				street: me.address?.street || '',
				zipCode: me.address?.zipCode || ''
			}),
			[userAddressCountry, me.address?.city, me.address?.region, me.address?.street, me.address?.zipCode]
		),
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(usePostalAddressVerificationSchema())
	});

	// Resetting the address when we get users' address
	useEffect(() => {
		if (me.address && userAddressCountry)
			postalAddressVerificationForm.reset({
				city: me.address?.city,
				country: userAddressCountry,
				region: me.address?.region,
				street: me.address?.street,
				zipCode: me.address?.zipCode
			});
	}, [
		postalAddressVerificationForm,
		me.address?.street,
		me.address?.city,
		me.address?.region,
		me.address?.zipCode,
		me.address,
		userAddressCountry
	]);

	return postalAddressVerificationForm;
};

export const usePostalAddressVerification = () => {
	const [index, setIndex] = useState(0);
	const [uploadedFiles, setUploadedFiles] = useState<Array<FileToUpload>>([]);
	const stepperRef = useRef<StepperRef>(null);
	const isAlertVisible =
		uploadedFiles.length > PROOF_OF_ADDRESS_UPLOAD_PROPS.selectionLimit ||
		uploadedFiles.some(file => file.size && file.size > PROOF_OF_ADDRESS_UPLOAD_PROPS.maxSize);

	const { data: userAddress, isLoading: isUserAddressLoading } = useUserAddressQuery();
	const { isLoading: areCountriesLoading } = useCountries();

	const {
		isLoading: isUpdatePostalAddressDataLoading,
		isSuccess: isUpdatePostalAddressDataSuccessful,
		mutate: updateUserAddressData
	} = useUpdateUserAddress();

	const {
		isLoading: isSubmitPostalAddressDocumentsLoading,
		isSuccess: isSubmitPostalAddressDocumentsSuccessful,
		mutate: submitPostalAddressDocuments
	} = useCreateValidationRequest();

	const postalAddressVerificationForm = usePostalAddressVerificationForm();

	const disabledFirstButton = useMemo(() => {
		switch (index) {
			case 0:
				return !postalAddressVerificationForm.formState.isValid || isUpdatePostalAddressDataLoading;
			case 1:
				return uploadedFiles.length === 0 || isAlertVisible || isSubmitPostalAddressDocumentsLoading;
			default:
				return false;
		}
	}, [
		index,
		isSubmitPostalAddressDocumentsLoading,
		isUpdatePostalAddressDataLoading,
		postalAddressVerificationForm.formState.isValid,
		isAlertVisible,
		uploadedFiles
	]);

	return {
		disabledFirstButton,
		index,
		isFormLoading: areCountriesLoading || isUserAddressLoading,
		isSubmitPostalAddressDocumentsLoading,
		isSubmitPostalAddressDocumentsSuccessful,
		isUpdatePostalAddressDataLoading,
		isUpdatePostalAddressDataSuccessful,
		postalAddressVerificationForm,
		setIndex,
		setUploadedFiles,
		stepperRef,
		submitPostalAddressDocuments,
		updateUserAddressData,
		uploadedFiles,
		userAddress
	};
};
